import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { CustomMapping, CustomMappingField } from '../types/mapping';
import { useCommonOptions } from '../hooks/useCommonOptions';
import { useDynamicMappings } from '../hooks/useDynamicMappings';
import { dineroFinanceColumns } from '../constants/DineroContants';

type FieldOption = {
  value: string;
  label: string;
  options?: { value: string; label: string }[];
  activeExtraCondition?: boolean;
  extrColumnsOptions?: { value: string; label: string }[];
};

type CustomMappingManagerProps = {
  open: boolean;
  onClose: () => void;
  onSave: (mapping: CustomMapping) => void;
  onDelete?: (mapping: CustomMapping) => void;
  existingMappings: CustomMapping[];
  field?: string;
};

const CustomMappingManager: React.FC<CustomMappingManagerProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  existingMappings,
  field
}) => {
  const [description, setDescription] = useState('');
  const [selectedField, setSelectedField] = useState(field || '');
  const [mappingFields, setMappingFields] = useState<CustomMappingField[]>([]);
  const [editingMapping, setEditingMapping] = useState<CustomMapping | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<CustomMapping | null>(null);
  const { currencies, paymentTerms, vatZones, customerGroups, units, entryTypeOptions, vatCodes, accountTypeOptions } = useCommonOptions();
  const { mappings } = useDynamicMappings();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isActiveExtraCondition, setIsActiveExtraCondition] = useState(false);

  const specialValueOptions = [
    { value: '', label: 'Custom Value' },
    { value: 'empty', label: 'Empty' },
    { value: 'not_empty', label: 'Not Empty' }
  ];

  const formControlStyles = {
    '& .MuiInputBase-root': {
      height: '28px',
      fontSize: '0.875rem',
      padding: '4px 8px'
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem'
    },
    '& .MuiMenuItem-root': {
      fontSize: '0.875rem',
      minHeight: '28px',
      padding: '4px 8px'
    }
  };

  const handleAddMapping = () => {
    setMappingFields([
      ...mappingFields,
      {
        field: selectedField,
        value: '',
        mappedValue: '',
        combinedConditions: []
      },
    ]);
  };

  const handleUpdateMapping = (index: number, key: keyof CustomMappingField, value: any) => {
    const updatedFields = [...mappingFields];
    const fieldOptions = getFieldOptions().find(f => f.value === selectedField)?.options || [];

    if (key === 'value') {
      updatedFields[index] = {
        ...updatedFields[index],
        value: value as string
      };
    } else if (key === 'mappedValue') {
      const option = fieldOptions.find(opt => opt.label === value);
      updatedFields[index] = {
        ...updatedFields[index],
        mappedValue: option ? option.value : value as string,
        originalMappedValue: option ? option.value : value as string,
        mappedValueLabel: value as string
      };
    } else if (key === 'combinedConditions') {
      updatedFields[index] = {
        ...updatedFields[index],
        combinedConditions: value as { column: string; value: string }[]
      };
    } else {
      updatedFields[index] = {
        ...updatedFields[index],
        [key]: value
      };
    }

    setMappingFields(updatedFields);
  };

  const handleAddCombinedCondition = (index: number) => {
    const updatedFields = [...mappingFields];
    updatedFields[index] = {
      ...updatedFields[index],
      combinedConditions: [
        ...(updatedFields[index].combinedConditions || []),
        { column: '', value: '' }
      ]
    };
    setMappingFields(updatedFields);
  };

  const handleRemoveCombinedCondition = (index: number, conditionIndex: number) => {
    const updatedFields = [...mappingFields];
    updatedFields[index] = {
      ...updatedFields[index],
      combinedConditions: updatedFields[index].combinedConditions?.filter((_, i) => i !== conditionIndex)
    };
    setMappingFields(updatedFields);
  };

  const handleUpdateCombinedCondition = (index: number, conditionIndex: number, field: 'column' | 'value', value: string) => {
    const updatedFields = [...mappingFields];
    const conditions = [...(updatedFields[index].combinedConditions || [])];
    conditions[conditionIndex] = {
      ...conditions[conditionIndex],
      [field]: value
    };
    updatedFields[index] = {
      ...updatedFields[index],
      combinedConditions: conditions
    };
    setMappingFields(updatedFields);
  };

  const getFieldOptions = (): FieldOption[] => {
    return [
      {
        value: "vatZone_vatZoneNumber",
        label: "VAT Zone",
        options: Object.entries(mappings.vatZones).map(([value, label]) => ({
          value,
          label
        })),
        activeExtraCondition: false
      },
      {
        value: "paymentTerms_paymentTermsNumber",
        label: "Payment Terms",
        options: Object.entries(mappings.paymentTerms).map(([value, label]) => ({
          value,
          label
        })),
        activeExtraCondition: false
      },
      {
        value: "customerGroup_customerGroupNumber",
        label: "Customer Group",
        options: Object.entries(mappings.customerGroups).map(([value, label]) => ({
          value,
          label
        })),
      },
      {
        value: "unit_unitNumber",
        label: "Unit",
        options: Object.entries(mappings.units).map(([value, label]) => ({
          value,
          label
        }))
      },
      {
        value: "entryTypeNumber",
        label: "Entry Type",
        options: entryTypeOptions.map(({ value, label }) => ({
          value,
          label
        })),
        activeExtraCondition: true,
        extrColumnsOptions: dineroFinanceColumns.map(({ id, label }) => ({
          value: id,
          label
        }))
      },
      {
        value: "vatCode",
        label: "VAT Code",
        options: vatCodes.map(({ value, label }) => ({
          value,
          label
        })),
        activeExtraCondition: false
      },  
      {
        value: "accountType",
        label: "Account Type",
        options: accountTypeOptions.map(({ value, label }) => ({
          value,
          label
        })),
        activeExtraCondition: false
      },
      // {
      //   value: "currency",
      //   label: "Currency",
      //   options: Object.entries(mappings.currencies).map(([value, label]) => ({
      //     value,
      //     label
      //   }))
      // }
    ];
  };

  // Filter out fields that already have mappings
  const getAvailableFields = () => {
    const allFields = getFieldOptions();
    return allFields.filter(
      field => !existingMappings.some(mapping => mapping.field === field.value && mapping.id !== editingMapping?.id)
    );
  };

  const handleSave = async () => {
    setIsUpdating(true);
    const mapping: CustomMapping = {
      id: editingMapping?.id || Date.now().toString(),
      field: selectedField,
      description,
      mappings: mappingFields.map(field => ({
        field: field.value,
        value: field.originalMappedValue || field.mappedValue,
        extraFields: field.combinedConditions?.map(cond => cond.column),
        extraValues: field.combinedConditions?.map(cond => cond.value)
      }))
    };
    await onSave(mapping);
    setDescription('');
    setSelectedField(field || '');
    setMappingFields([]);
    setEditingMapping(null);
    setActiveTab(0);
    setIsUpdating(false);
  };

  const handleClose = () => {
    handleCancel();
    onClose();
  };

  const handleCancel = () => {
    setDescription('');
    setSelectedField(field || '');
    setMappingFields([]);
    setEditingMapping(null);
    setActiveTab(0);
    //
  }

  const handleEditMapping = (mapping: CustomMapping) => {
    setEditingMapping(mapping);
    setActiveTab(1);
  };

  const handleDeleteClick = (mapping: CustomMapping) => {
    setMappingToDelete(mapping);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (mappingToDelete && onDelete) {
      onDelete(mappingToDelete);
    }
    setDeleteConfirmOpen(false);
    setMappingToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setMappingToDelete(null);
  };

  const getFieldLabel = (field: string) => {
    const fieldOption = getFieldOptions().find(f => f.value === field);
    return fieldOption ? fieldOption.label : field;
  };

  const handleRemoveMapping = (index: number) => {
    setMappingFields(mappingFields.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (editingMapping) {
      setDescription(editingMapping.description || '');
      setSelectedField(editingMapping.field);
      const fieldOptions = getFieldOptions().find(f => f.value === editingMapping.field)?.options || [];
      const mappings = Array.isArray(editingMapping.mappings) ? editingMapping.mappings : [];
      const fields: CustomMappingField[] = mappings.map(mapping => {
        const option = fieldOptions.find(opt => opt.value === mapping.value);
        const combinedConditions = mapping.extraFields?.map((field, index) => ({
          column: field,
          value: mapping.extraValues?.[index] || ''
        })) || [];
        
        return {
          field: editingMapping.field,
          value: mapping.field,
          mappedValue: mapping.value,
          originalMappedValue: mapping.value,
          mappedValueLabel: option ? option.label : mapping.value,
          combinedConditions
        };
      });
      setMappingFields(fields);
    } else {
      setDescription('');
      setSelectedField(field || '');
      setMappingFields([]);
    }
    const fieldEditing = getFieldOptions().find(f => f.value === editingMapping?.field);
    setIsActiveExtraCondition(fieldEditing?.activeExtraCondition || false);
  }, [editingMapping, field]);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMapping ? "Edit Cell Mapping" : "Create New Cell Mapping"}
        </DialogTitle>
        <DialogContent>
          <Tabs value={activeTab} onChange={(_, newValue) => {setActiveTab(newValue);}} sx={{ mb: 2 }}>
            <Tab label="Existing Mappings" onClick={()=>{
              handleCancel();
            }} />
            <Tab label={editingMapping ? "Edit Cell Mapping" : "Create New Cell Mapping"} />
          </Tabs>

          {activeTab === 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.values(existingMappings).length > 0 ? Object.values(existingMappings).map((mapping) => (
                    <TableRow key={mapping.id}>
                      <TableCell>{getFieldLabel(mapping.field)}</TableCell>
                      <TableCell>{mapping.description}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleEditMapping(mapping)}>
                          <EditIcon />
                        </IconButton>
                        {onDelete && (
                          <IconButton onClick={() => handleDeleteClick(mapping)}>
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  )) : <TableRow><TableCell align="center" colSpan={3}>No mappings found</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <>
              {!field && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Field</InputLabel>
                    <Select
                      value={selectedField}
                      onChange={(e) => {
                        setSelectedField(e.target.value);
                        const fieldEditing = getFieldOptions().find(f => f.value === e.target.value);
                        setIsActiveExtraCondition(fieldEditing?.activeExtraCondition || false);
                      }}
                      label="Field"
                      disabled={!!editingMapping}
                    >
                      {getAvailableFields().map((field) => (
                        <MenuItem key={field.value} value={field.value}>
                          {field.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddMapping}
                  disabled={!selectedField}
                >
                  Add Mapping
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, px: 1 }}>Dinero current value</TableCell>
                      {isActiveExtraCondition && (
                        <>
                          <TableCell sx={{ py: 0.5, px: 1 }}>Dinero Extra Column</TableCell>
                        </>
                      )}
                      <TableCell sx={{ py: 0.5, px: 1 }}>e-conomic Value</TableCell>
                      <TableCell width={40} sx={{ py: 0.5, px: 1 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappingFields.map((field, index) => {
                      const fieldOptions = getFieldOptions().find(f => f.value === field.field);
               
                      const options = fieldOptions?.options || [];
                      const extraColumnsOptions = fieldOptions?.extrColumnsOptions || [];
                      return (
                        <TableRow key={index}>
                          <TableCell sx={{ py: 0.5, px: 1 }}>
                            <TextField
                              value={field.value}
                              onChange={(e) => handleUpdateMapping(index, 'value', e.target.value)}
                              size="small"
                              fullWidth
                              margin="none"
                              sx={formControlStyles}
                            />
                          </TableCell>
                          {isActiveExtraCondition && (
                            <>
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleAddCombinedCondition(index)}
                                  sx={{ mb: 0.5, height: '28px', minWidth: '120px', padding: '0 8px' }}
                                >
                                  Add Condition
                                </Button>
                                {field.combinedConditions?.map((condition, conditionIndex) => (
                                  <Box key={conditionIndex} sx={{ mt: 0.5 }}>
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
                                      <FormControl fullWidth size="small" margin="none" sx={formControlStyles}>
                                        <Select
                                          value={condition.column}
                                          onChange={(e) => handleUpdateCombinedCondition(index, conditionIndex, 'column', e.target.value)}
                                          size="small"
                                        >
                                          <MenuItem value="">Select Column</MenuItem>
                                          {extraColumnsOptions.map((column) => (
                                            <MenuItem key={column.value} value={column.value}>
                                              {column.label}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRemoveCombinedCondition(index, conditionIndex)}
                                        sx={{ height: '28px', width: '28px', padding: '4px' }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                      <FormControl size="small" margin="none" sx={{ ...formControlStyles, minWidth: 150 }}>
                                        <Select
                                          value={condition.value === 'empty' || condition.value === 'not_empty' ? condition.value : ''}
                                          onChange={(e) => {
                                            if (e.target.value === 'empty' || e.target.value === 'not_empty') {
                                              handleUpdateCombinedCondition(index, conditionIndex, 'value', e.target.value);
                                            } else {
                                              handleUpdateCombinedCondition(index, conditionIndex, 'value', '');
                                            }
                                          }}
                                          size="small"
                                        >
                                          {specialValueOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                              {option.label}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                      <TextField
                                        value={condition.value === 'empty' || condition.value === 'not_empty' ? '' : condition.value}
                                        onChange={(e) => handleUpdateCombinedCondition(index, conditionIndex, 'value', e.target.value)}
                                        size="small"
                                        placeholder="Value"
                                        disabled={condition.value === 'empty' || condition.value === 'not_empty'}
                                        fullWidth
                                        margin="none"
                                        sx={formControlStyles}
                                      />
                                    </Box>
                                  </Box>
                                ))}
                              </TableCell>
                            </>
                          )}
                          <TableCell sx={{ py: 0.5, px: 1 }}>
                            {options.length > 0 ? (
                              <FormControl fullWidth size="small" margin="none" sx={formControlStyles}>
                                <Select
                                  value={field.mappedValueLabel || field.mappedValue}
                                  onChange={(e) => handleUpdateMapping(index, 'mappedValue', e.target.value)}
                                  size="small"
                                >
                                  {options.map((option) => (
                                    <MenuItem key={option.value} value={option.label}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <TextField
                                value={field.mappedValue}
                                onChange={(e) => handleUpdateMapping(index, 'mappedValue', e.target.value)}
                                size="small"
                                fullWidth
                                margin="none"
                                sx={formControlStyles}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, px: 1 }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleRemoveMapping(index)}
                              sx={{ height: '28px', width: '28px', padding: '4px' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={activeTab === 0 ? handleClose : handleCancel}>Cancel</Button>
          {activeTab === 1 && (
            <Button onClick={handleSave} disabled={isUpdating}>
              {isUpdating ? <CircularProgress size={20} /> : editingMapping ? "Update" : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this mapping?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomMappingManager;
